import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { MdAdd } from "react-icons/md";
import useApiRequest from "../../../../../utils/hooks/useApiRequest";
import useFacility from "../../../../../utils/hooks/useFacility";
import { GET_LINKED_ACCOUNTS_URL } from "../../../../api/apiRoutes";
import { ADD_SUBACCOUNT_PAGE } from "../../../../routes";
import { LinkedAccount } from "../../../../models/LinkedAccount";
import EASkeletonLoader from "../../../shared/EASkeletonLoader";
import EAAlert from "../../../shared/EAAlert";
import EAButton from "../../../shared/Buttons/EAButton";
import EALinkedPersonRow from "../../../shared/EALinkedPersonRow/EALinkedPersonRow";
import LinkedPersonDetailsModal from "./LinkedPersonDetailsModal";
import { mergeSearchWithTab } from "../../../../../utils/myAccountTabUrl";
import "./PeopleTab.scss";

const PeopleTab: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const facilityIds = useFacility();
  const { get } = useApiRequest();

  const [accounts, setAccounts] = useState<LinkedAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailAccount, setDetailAccount] = useState<LinkedAccount | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await get(GET_LINKED_ACCOUNTS_URL(facilityIds));
      if (res?.isError) {
        setError((res.errorTable && res.errorTable[0]) || t("my_account_people_load_error"));
        setAccounts([]);
        return;
      }
      const raw = res?.result;
      const list: LinkedAccount[] = Array.isArray(raw) ? raw : [];
      setAccounts(list);
    } catch {
      setError(t("my_account_people_load_error"));
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, [facilityIds, get, t]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const handleOpenDetails = (account: LinkedAccount) => {
    setDetailAccount(account);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setDetailAccount(null);
  };

  return (
    <div className="tab-content__container people-tab">
      <div className="people-tab__intro">
        <h5 className="people-tab__title">{t("my_account_people_title")}</h5>
        <p className="people-tab__description body-text-small text-color-dark-gray">
          {t("my_account_people_description")}
        </p>
      </div>

      <div className="people-tab__add">
        <EAButton
          title={t("my_account_people_add")}
          variant="outlined"
          color="primary"
          startIcon={<MdAdd />}
          onClick={() =>
            navigate(`${ADD_SUBACCOUNT_PAGE}?${mergeSearchWithTab(location.search, "people")}`)
          }
        />
      </div>

      {loading && <EASkeletonLoader height="120px" />}

      {!loading && error && <EAAlert type="error" message={error} />}

      {!loading && !error && accounts.length === 0 && (
        <p className="body-text text-color-dark-gray">{t("my_account_people_empty")}</p>
      )}

      {!loading && !error && accounts.length > 0 && (
        <div className="list-container">
          <div className="list-wrapper">
            {accounts.map((account, index) => (
              <EALinkedPersonRow
                key={`${account.objectId ?? index}-${account.objectDb ?? 0}-${index}`}
                account={account}
                onDetails={() => handleOpenDetails(account)}
              />
            ))}
          </div>
        </div>
      )}

      <LinkedPersonDetailsModal
        key={
          detailAccount
            ? `linked-person-${String(detailAccount.objectId)}-${String(detailAccount.objectDb)}`
            : "closed"
        }
        isOpen={modalOpen}
        onClose={handleCloseModal}
        account={detailAccount}
        onSaved={loadAccounts}
        onDeleted={loadAccounts}
      />
    </div>
  );
};

export default PeopleTab;
